import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { httpRequest } from '../../config/api';
import { ACTIONS, RESOURCE_TYPES, STATUSES } from './constants';

// ─── Timespan presets ────────────────────────────────────────────────────────────

const TIMESPAN_PRESETS = [
  { key: '1h', label: 'Last 1 hour', ms: 1 * 60 * 60 * 1000 },
  { key: '6h', label: 'Last 6 hours', ms: 6 * 60 * 60 * 1000 },
  { key: '24h', label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
  { key: '2d', label: 'Last 2 days', ms: 2 * 24 * 60 * 60 * 1000 },
  { key: '3d', label: 'Last 3 days', ms: 3 * 24 * 60 * 60 * 1000 },
  { key: '7d', label: 'Last week', ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '14d', label: 'Last 2 weeks', ms: 14 * 24 * 60 * 60 * 1000 },
  { key: '30d', label: 'Last month', ms: 30 * 24 * 60 * 60 * 1000 },
  { key: 'custom', label: 'Custom', ms: null }
];

const DEFAULT_PRESET = '2d';

const pad = n => String(n).padStart(2, '0');

const toDateTimeLocal = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
  `T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const presetToIso = ms => new Date(Date.now() - ms).toISOString();

// ─── Styled ─────────────────────────────────────────────────────────────────────

const Bar = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Select = styled.select`
  padding: 7px 10px;
  height: 34px;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  background: white;
  cursor: pointer;
  min-width: 140px;
  max-width: 200px;

  &:focus {
    outline: none;
    border-color: #228be6;
    box-shadow: 0 0 0 2px rgba(34, 139, 230, 0.15);
  }
`;

// ─── Searchable combobox for Project ────────────────────────────────────────────

const ComboWrapper = styled.div`
  position: relative;
  min-width: 140px;
  max-width: 200px;
`;

const ComboInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  height: 34px;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #228be6;
    box-shadow: 0 0 0 2px rgba(34, 139, 230, 0.15);
  }
`;

const ComboDropdown = styled.ul`
  position: absolute;
  top: calc(100% + 3px);
  left: 0;
  right: 0;
  z-index: 300;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 220px;
  overflow-y: auto;
  margin: 0;
  padding: 4px 0;
  list-style: none;
`;

const ComboOption = styled.li`
  padding: 7px 10px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover,
  &[data-active='true'] {
    background: #e7f5ff;
    color: #1c7ed6;
  }
`;

const TextInput = styled.input`
  padding: 7px 10px;
  height: 34px;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;

  &:focus {
    outline: none;
    border-color: #228be6;
    box-shadow: 0 0 0 2px rgba(34, 139, 230, 0.15);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-wrap: wrap;
  flex: 1;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-left: auto;
`;

const Btn = styled.button`
  padding: 7px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;

  ${props =>
    props.$variant === 'ghost'
      ? `
      background: white;
      border-color: #d0ebff;
      color: #228BE6;
      &:hover { background: #e7f5ff; border-color: #228BE6; }
    `
      : `
      background: #228BE6;
      color: white;
      &:hover { background: #1c7ed6; }
    `}
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
  padding-bottom: 8px;
`;

// ─── Timespan chip ───────────────────────────────────────────────────────────────

const TimespanWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TimespanChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  height: 34px;
  box-sizing: border-box;
  border: 1px solid ${props => (props.$active ? '#228BE6' : '#d1d5db')};
  border-radius: 6px;
  background: ${props => (props.$active ? '#e7f5ff' : 'white')};
  color: ${props => (props.$active ? '#1c7ed6' : '#374151')};
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;

  &:hover {
    border-color: #228be6;
    background: #f0f8ff;
  }
`;

const ChipCaret = styled.span`
  font-size: 10px;
  opacity: 0.7;
  margin-left: 2px;
`;

// ─── Timespan popover ────────────────────────────────────────────────────────────

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 200;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 280px;
`;

const PopoverBody = styled.div`
  display: flex;
`;

const PopoverLeft = styled.div`
  padding: 16px 12px;
  min-width: 160px;
`;

const PopoverRight = styled.div`
  padding: 16px 14px;
  border-left: 1px solid #f3f4f6;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PopoverTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 10px;
`;

const RadioList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  transition: background 0.1s;

  &:hover {
    background: #f3f4f6;
  }

  input[type='radio'] {
    accent-color: #228be6;
    width: 15px;
    height: 15px;
  }
`;

const CustomFieldLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const CustomInput = styled.input`
  width: 100%;
  padding: 6px 9px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #228be6;
    box-shadow: 0 0 0 2px rgba(34, 139, 230, 0.15);
  }
`;

const PopoverFooter = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #f3f4f6;
  justify-content: flex-end;
`;

// ─── Component ──────────────────────────────────────────────────────────────────

export const getDefaultFilters = () => ({
  search: '',
  from_date: presetToIso(TIMESPAN_PRESETS.find(p => p.key === DEFAULT_PRESET).ms),
  to_date: '',
  actions: [],
  resource_types: [],
  statuses: [],
  my_actions_only: false,
  sort_order: 'desc',
  project_id: '',
  period_id: '',
  user_id: ''
});

const ActivityLogFilters = ({ onApply }) => {
  const [f, setF] = useState(getDefaultFilters);

  // Projects list for the filter dropdown (each project already includes its periods)
  const { data: workflowsData } = useQuery(
    ['AUDIT_FILTER_PROJECTS'],
    () => httpRequest.get('api/workflows/all'),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );
  const projects = Array.isArray(workflowsData) ? workflowsData : [];

  // Initiators list
  const { data: initiatorsData } = useQuery(
    ['AUDIT_FILTER_INITIATORS'],
    () => httpRequest.get('api/audit-logs/initiators'),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );
  const initiators = Array.isArray(initiatorsData) ? initiatorsData : [];

  // Fetch full workflow details for the selected project so we get period names + years
  const { data: selectedWorkflow } = useQuery(
    ['AUDIT_SELECTED_WORKFLOW', f.project_id],
    () => httpRequest.get(`api/workflows/${f.project_id}`),
    { enabled: !!f.project_id, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );
  const periods = selectedWorkflow?.periods ?? [];

  // Searchable project combobox state
  const [projectSearch, setProjectSearch] = useState('');
  const [comboOpen, setComboOpen] = useState(false);
  const comboRef = useRef(null);

  // Initiated By combobox state
  const [initiatorSearch, setInitiatorSearch] = useState('');
  const [initiatorComboOpen, setInitiatorComboOpen] = useState(false);
  const initiatorComboRef = useRef(null);

  const filteredInitiators = initiatorSearch
    ? initiators.filter(u => u.user_name.toLowerCase().includes(initiatorSearch.toLowerCase()))
    : initiators;

  const filteredProjects = projectSearch
    ? projects.filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase()))
    : projects;

  // Close combobox on outside click
  useEffect(() => {
    if (!comboOpen) return;
    const handler = e => {
      if (comboRef.current && !comboRef.current.contains(e.target)) {
        setComboOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [comboOpen]);

  // Close initiator combobox on outside click
  useEffect(() => {
    if (!initiatorComboOpen) return;
    const handler = e => {
      if (initiatorComboRef.current && !initiatorComboRef.current.contains(e.target)) {
        setInitiatorComboOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [initiatorComboOpen]);

  const selectProject = project => {
    setF(prev => ({ ...prev, project_id: project ? project.id : '', period_id: '' }));
    setProjectSearch(project ? project.name : '');
    setComboOpen(false);
  };

  // Timespan popover
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [draftPreset, setDraftPreset] = useState(DEFAULT_PRESET);
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const [appliedPreset, setAppliedPreset] = useState(DEFAULT_PRESET);
  const popoverRef = useRef(null);
  const chipRef = useRef(null);

  // Seed custom draft times when Custom is first chosen
  useEffect(() => {
    if (draftPreset === 'custom' && !draftStart) {
      const now = new Date();
      setDraftEnd(toDateTimeLocal(now));
      setDraftStart(toDateTimeLocal(new Date(now - 24 * 60 * 60 * 1000)));
    }
  }, [draftPreset]);

  // Close popover on outside click
  useEffect(() => {
    if (!popoverOpen) return;
    const handler = e => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        chipRef.current &&
        !chipRef.current.contains(e.target)
      ) {
        setPopoverOpen(false);
        setDraftPreset(appliedPreset);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popoverOpen, appliedPreset]);

  const set = (key, value) => setF(prev => ({ ...prev, [key]: value }));

  const handleReset = () => {
    const freshDefaults = getDefaultFilters();
    setF(freshDefaults);
    setAppliedPreset(DEFAULT_PRESET);
    setDraftPreset(DEFAULT_PRESET);
    setDraftStart('');
    setDraftEnd('');
    setProjectSearch('');
    setInitiatorSearch('');
    onApply(freshDefaults);
  };

  // When project changes, clear period_id
  const handleProjectChange = e => {
    setF(prev => ({ ...prev, project_id: e.target.value, period_id: '' }));
  };

  const handleApplyFilters = () => onApply({ ...f });

  const handleTimespanApply = () => {
    const preset = TIMESPAN_PRESETS.find(p => p.key === draftPreset);
    const from =
      draftPreset === 'custom'
        ? draftStart
          ? new Date(draftStart).toISOString()
          : ''
        : preset?.ms
          ? presetToIso(preset.ms)
          : '';
    const to = draftPreset === 'custom' ? (draftEnd ? new Date(draftEnd).toISOString() : '') : '';
    setAppliedPreset(draftPreset);
    setF(prev => ({ ...prev, from_date: from, to_date: to }));
    setPopoverOpen(false);
  };

  const handleTimespanCancel = () => {
    setDraftPreset(appliedPreset);
    setPopoverOpen(false);
  };

  const appliedLabel = TIMESPAN_PRESETS.find(p => p.key === appliedPreset)?.label ?? 'Select';

  return (
    <Bar>
      <Row>
        {/* Search */}
        <FilterGroup>
          <Label>Search</Label>
          <TextInput
            placeholder="Resource type, name, operation…"
            value={f.search}
            onChange={e => set('search', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleApplyFilters()}
            style={{ minWidth: 220 }}
          />
        </FilterGroup>

        {/* Timespan */}
        <FilterGroup>
          <Label>Timespan</Label>
          <TimespanWrapper>
            <TimespanChip
              ref={chipRef}
              type="button"
              aria-label="Timespan"
              $active={popoverOpen}
              onClick={() => setPopoverOpen(v => !v)}
            >
              <strong>{appliedLabel}</strong>
              <ChipCaret>{popoverOpen ? '▲' : '▼'}</ChipCaret>
            </TimespanChip>

            {popoverOpen && (
              <Popover ref={popoverRef}>
                <PopoverBody>
                  <PopoverLeft>
                    <PopoverTitle>Timespan</PopoverTitle>
                    <RadioList>
                      {TIMESPAN_PRESETS.map(preset => (
                        <RadioOption key={preset.key}>
                          <input
                            type="radio"
                            name="timespan"
                            value={preset.key}
                            checked={draftPreset === preset.key}
                            onChange={() => setDraftPreset(preset.key)}
                          />
                          {preset.label}
                        </RadioOption>
                      ))}
                    </RadioList>
                  </PopoverLeft>

                  {draftPreset === 'custom' && (
                    <PopoverRight>
                      <div>
                        <CustomFieldLabel>Start time</CustomFieldLabel>
                        <CustomInput
                          type="datetime-local"
                          value={draftStart}
                          onChange={e => setDraftStart(e.target.value)}
                        />
                      </div>
                      <div>
                        <CustomFieldLabel>End time</CustomFieldLabel>
                        <CustomInput
                          type="datetime-local"
                          value={draftEnd}
                          onChange={e => setDraftEnd(e.target.value)}
                        />
                      </div>
                    </PopoverRight>
                  )}
                </PopoverBody>

                <PopoverFooter>
                  <Btn $variant="ghost" type="button" onClick={handleTimespanCancel}>
                    Cancel
                  </Btn>
                  <Btn type="button" onClick={handleTimespanApply}>
                    Apply
                  </Btn>
                </PopoverFooter>
              </Popover>
            )}
          </TimespanWrapper>
        </FilterGroup>

        {/* Project — searchable combobox */}
        <FilterGroup>
          <Label>Project</Label>
          <ComboWrapper ref={comboRef}>
            <ComboInput
              placeholder="All projects"
              value={projectSearch}
              onChange={e => {
                setProjectSearch(e.target.value);
                setComboOpen(true);
                // If user clears the field, also clear the filter
                if (!e.target.value) setF(prev => ({ ...prev, project_id: '', period_id: '' }));
              }}
              onFocus={() => setComboOpen(true)}
            />
            {comboOpen && (
              <ComboDropdown>
                <ComboOption onMouseDown={() => selectProject(null)} data-active={!f.project_id}>
                  All projects
                </ComboOption>
                {filteredProjects.map(p => (
                  <ComboOption
                    key={p.id}
                    onMouseDown={() => selectProject(p)}
                    data-active={f.project_id === p.id}
                  >
                    {p.name}
                  </ComboOption>
                ))}
              </ComboDropdown>
            )}
          </ComboWrapper>
        </FilterGroup>

        {/* Period */}
        <FilterGroup>
          <Label>Period</Label>
          <Select
            value={f.period_id}
            onChange={e => set('period_id', e.target.value)}
            disabled={!f.project_id}
          >
            <option value="">All periods</option>
            {periods.map(p => (
              <option key={p.id} value={p.id}>
                {p.period} {p.year}
              </option>
            ))}
          </Select>
        </FilterGroup>

        {/* Action type */}
        <FilterGroup>
          <Label>Action</Label>
          <Select
            value={f.actions[0] ?? ''}
            onChange={e => set('actions', e.target.value ? [e.target.value] : [])}
          >
            <option value="">All actions</option>
            {ACTIONS.map(a => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Select>
        </FilterGroup>

        {/* Resource type */}
        <FilterGroup>
          <Label>Resource type</Label>
          <Select
            value={f.resource_types[0] ?? ''}
            onChange={e => set('resource_types', e.target.value ? [e.target.value] : [])}
          >
            <option value="">All resources</option>
            {RESOURCE_TYPES.map(r => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </FilterGroup>

        {/* Status */}
        <FilterGroup>
          <Label>Status</Label>
          <Select
            value={f.statuses[0] ?? ''}
            onChange={e => set('statuses', e.target.value ? [e.target.value] : [])}
          >
            <option value="">All statuses</option>
            {STATUSES.map(s => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </FilterGroup>

        {/* Initiated By */}
        <FilterGroup>
          <Label>Initiated by</Label>
          <ComboWrapper ref={initiatorComboRef}>
            <ComboInput
              placeholder="All users"
              value={initiatorSearch}
              onChange={e => {
                setInitiatorSearch(e.target.value);
                setInitiatorComboOpen(true);
                if (!e.target.value) setF(prev => ({ ...prev, user_id: '' }));
              }}
              onFocus={() => setInitiatorComboOpen(true)}
            />
            {initiatorComboOpen && (
              <ComboDropdown>
                <ComboOption
                  onMouseDown={() => {
                    setF(prev => ({ ...prev, user_id: '' }));
                    setInitiatorSearch('');
                    setInitiatorComboOpen(false);
                  }}
                  data-active={!f.user_id}
                >
                  All users
                </ComboOption>
                {filteredInitiators.map(u => (
                  <ComboOption
                    key={u.user_id}
                    onMouseDown={() => {
                      setF(prev => ({ ...prev, user_id: u.user_id }));
                      setInitiatorSearch(u.user_name);
                      setInitiatorComboOpen(false);
                    }}
                    data-active={f.user_id === u.user_id}
                  >
                    {u.user_name}
                  </ComboOption>
                ))}
              </ComboDropdown>
            )}
          </ComboWrapper>
        </FilterGroup>

        {/* Sort order */}
        <FilterGroup>
          <Label>Sort</Label>
          <Select value={f.sort_order} onChange={e => set('sort_order', e.target.value)}>
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </Select>
        </FilterGroup>

        {/* My actions */}
        <CheckLabel>
          <input
            type="checkbox"
            checked={f.my_actions_only}
            onChange={e => set('my_actions_only', e.target.checked)}
          />
          My actions only
        </CheckLabel>
      </Row>

      <ActionRow>
        <Btn $variant="ghost" onClick={handleReset}>
          Reset
        </Btn>
        <Btn onClick={handleApplyFilters}>Apply filters</Btn>
      </ActionRow>
    </Bar>
  );
};

export default ActivityLogFilters;
